import { HabitHeroMessages, JSNumberToDayMapping } from "../../Constants";
import { calculateDayColor, getEntriesForWeek, getWeek, getWeekStartDate } from "../CalendarHelper";
import { getActiveStreak, getHabitHeroStreak, getLongestStreakOfDays } from "../StreaksHelper";
import { getActivityLevelsForWeek, getTopTimeOverlapClusters } from "./ActivityHelper";
import { getUplifterDepleterStories } from './RecurringCausesStoryHelper';
import { getCauseCountInCategory, getMostRecurringCausesInCategory } from "./MostRecurringCausesHelper";
import { getPeakAndValley } from "./PeakValleyHelper";
import { getReflection, createOrUpdateReflection } from "../../adapters/reflection-adapter";
import {
    isWeekInThePast,
    getInfoAboutWeekInMonth,
    getFlowCountDiff,
    getPleasantPercentDiff,
    getPleasantUnpleasantPercentages,
    getWorkLifeNetValues,
    getNumDaysInBetween,
    getRewardCounts,
    getEnergyLevelsChartData,
    formatDateForStorage,
    getStoryGPTData,
    getRandomNumber
} from "./WeekReviewHelper";

/** This is responsible for generating the data that users will see while reviewing a week of flows */
export class WeekReviewManager {
    reflectionQuestionsForFullerWeek = [
        `What does this reflection bring up for you? What do you want to bring into next week?`,
        `What does this reflection spark for you?`,
        `What's coming up for you as you reflect? What will you take into next week?`,
        `What did this reflection stir in you? What feels important to carry forward?`,
        `What resonated with you? Is there a learning you'd like take with you to next week?`,
        `Do you have a takeaway from this reflection? How might it guide you moving forward?`,
        `What feels most meaningful from this reflection?`
    ]

    reflectionQuestionsForSparseWeek = [
        `What else happened for you this week?`,
        `If this week had guiding word for you, what would it be?`,
        `What did you notice about yourself this week?`,
        `Did anything stand out to you this week?`,
        `If this week could leave a note for the next one, what would it say?`,
        `How would you sum up your week in a text to a friend?`,
        `Anything else you want to remember about this week?`
    ]

    weekTitleQuestions = [
        `What's your headline for this week?`,
        `How would you title this week?`,
        `What's your tagline for this week?`,
        `What was this week's headline?`,
        `How would you tagline this week?`,
        `How would you recall this week?`,
        `How would you caption this week?`,
    ]

    constructor(userId, weekStartDate, entries) {
        this.userId = userId;
        this.weekStartDate = weekStartDate;
        this.entries = entries;

        this.isWeekPast = isWeekInThePast(weekStartDate);
        this.currentWeekFlows = getEntriesForWeek(entries, this.weekStartDate);

        this.stories = null;
        this.numActiveDays = 0;
        this.orderedPeakValleyInfo = null;
        this.isReflectionSaved = false;
    }

    /**
     * @returns Array of component data in the order it will be shown: [{component: 'intro', heading: "", chartData: [...], subHeading: ""}, {}]
    */
    getDataForWeekReview() {
        let stories = [];

        const introData = this.getIntroStoryData(this.weekStartDate, this.currentWeekFlows);
        stories.push({
            component: "intro",
            data: introData,
            duration: 7000
        });

        stories.push({
            component: 'weekTitle',
            data: {
                question: this.weekTitleQuestions[getRandomNumber(0, 7)],
                answer: ''
            },
            duration: 7000
        });

        const pastWeekFlows = getEntriesForWeek(this.entries, new Date(this.weekStartDate.getFullYear(), this.weekStartDate.getMonth(), this.weekStartDate.getDate() - 7));
        const isFirstWeek = this.entries.length === this.currentWeekFlows.length;
        const flowCountStoryData = this.getFlowCountStoryData(this.currentWeekFlows, pastWeekFlows, isFirstWeek);
        stories.push(flowCountStoryData);

        const activityLevelsInfo = getActivityLevelsForWeek(this.currentWeekFlows);
        this.numActiveDays = activityLevelsInfo?.numActiveDays;
        stories.push({
            component: "activityByDay",
            data: activityLevelsInfo,
            duration: 7000
        })

        const mostCommonTimeWindows = getTopTimeOverlapClusters(this.currentWeekFlows)?.slice(0, 3);
        if (mostCommonTimeWindows.length > 0) {
            stories.push({
                component: 'mostCommonTimes',
                data: {
                    times: mostCommonTimeWindows,
                },
                duration: mostCommonTimeWindows.length > 2 ? 8000 : 5000
            })
        }

        const pleasantPercentageStoryData = this.getPleasantPercentageStoryData(this.currentWeekFlows, pastWeekFlows);
        stories.push(pleasantPercentageStoryData);

        const workLifeNumbersStory = getWorkLifeNetValues(this.currentWeekFlows);
        if (workLifeNumbersStory) {
            if (workLifeNumbersStory.data.work === null) {
                // that means all flows are about life. So we can show this info with the flow count story itself
                this.updateFlowCountStoryData(flowCountStoryData, 'life')
            }
            else if (workLifeNumbersStory.data.life === null) {
                this.updateFlowCountStoryData(flowCountStoryData, 'work');
            }
            else {
                stories.push(workLifeNumbersStory);
            }
        }

        stories.push({
            component: 'energyTrend', // the trend is fetched later
            data: { trend: "" },
            duration: 6500
        });

        const energyLevelsChartData = getEnergyLevelsChartData(this.currentWeekFlows);
        stories.push({
            component: 'energyLevels',
            data: { energyLevels: energyLevelsChartData },
            duration: 6000
        });

        stories.push({
            component: 'pleasantnessIntro'
        });

        stories.push({
            component: 'pleasantnessTrend',
            data: { trend: "" },
            duration: 7500
        });

        const peakValleyData = getPeakAndValley(this.currentWeekFlows);
        this.orderedPeakValleyInfo = peakValleyData.orderedPeakValleyInfo;
        stories.push({
            component: 'peakValley',
            data: {
                dataForChart: peakValleyData.dataForChart,
                peakIndices: peakValleyData.peakIndices,
                valleyIndices: peakValleyData.valleyIndices,
                sortedPeakAndValleyFlows: peakValleyData.sortedPeakAndValleyFlows,
                description: peakValleyData.description
            },
            duration: 10000
        });

        const mostRecurringUplifterDepleterStories = getUplifterDepleterStories(this.currentWeekFlows, 'recurringCauses');
        stories = stories.concat(mostRecurringUplifterDepleterStories);

        const recurringFeelings = this.getRecurringFeelingsStory(this.currentWeekFlows);
        if (recurringFeelings) {
            stories.push(recurringFeelings);
        }

        const supportStory = this.getSupportStory(this.currentWeekFlows);
        if (supportStory) {
            stories.push(supportStory);
        }

        this.updateStoryOrderWithRewards(stories, this.currentWeekFlows, flowCountStoryData);

        const randomIndex = getRandomNumber(0, 7); // we have 7 questions max currently.
        const reflectionQuestion = activityLevelsInfo.numActiveDays < 4 ? this.reflectionQuestionsForSparseWeek[randomIndex]
            : this.reflectionQuestionsForFullerWeek[randomIndex];
        stories.push({
            component: 'reflectionInput',
            data: {
                question: reflectionQuestion,
                answer: ''
            }
        });

        const streakStories = this.getStreakStories(this.entries, this.currentWeekFlows);
        this.updateStoryOrderWithStreaks(stories, streakStories);

        this.stories = stories.filter(item => item !== undefined);
        return this.stories;
    }

    //#region story order helper functions
    updateStoryOrderWithStreaks(stories, streakStories) {
        if (streakStories.length === 0) {
            return;
        }

        const streakStory = streakStories[0]; // habit hero story can never exist without a streak story, so the first one is always streak
        const streakStoryIndex = this.updateStoryOrderWithDayStreak(stories, streakStory, this.isWeekPast);
        if (streakStories.length > 1) {
            // aka habit hero story is available and should be added
            this.updateStoryOrderWithHabitHero(
                stories,
                streakStories[1],
                streakStory.data.isActiveHighlighted,
                streakStoryIndex,
                this.isWeekPast
            );
        }
    }

    updateStoryOrderWithDayStreak(stories, streakStory, isWeekPast) {
        let insertAtIndex;
        if (streakStory.data.isActiveHighlighted && !isWeekPast) {
            const reflectionStoryIndex = stories.findIndex(story => story.component === 'reflectionInput');
            // put it right before end reflection
            insertAtIndex = reflectionStoryIndex;
            stories.splice(insertAtIndex, 0, streakStory);
        }
        else {
            const flowCountStoryIndex = stories.findIndex(story => story.component === 'flowCount')
            // put it after num flows
            insertAtIndex = flowCountStoryIndex + 1
            stories.splice(insertAtIndex, 0, streakStory);
        }

        return insertAtIndex;
    }

    updateStoryOrderWithHabitHero(stories, habitHeroStory, isDayStreakActive, dayStreakStoryIndex) {
        // if there is not an active day streak but there is a habit hero streak, show it right after week title 
        // irrespective of current or past week 
        if (!isDayStreakActive && habitHeroStory.data.hasHabitHeroStreak) {
            // insert habit hero streak right after week title
            stories.splice(2, 0, habitHeroStory);
            return;
        }

        // when a single habit hero is present and we are in current week with an active day streak
        // show it after day streak whatever the case
        stories.splice(dayStreakStoryIndex + 1, 0, habitHeroStory);
    }

    updateStoryOrderWithRewards(stories, currentWeekFlows, flowCountStory) {
        const rewardsTally = getRewardCounts(currentWeekFlows)
            .filter(reward => !["Consistent", "HabitHero"].includes(reward[0])); // Remove these rewards from the array
        const numTotalRewards = rewardsTally.reduce((sum, tuple) => sum + tuple[1], 0);
        const dedicatedIndex = rewardsTally.findIndex(tuple => tuple[0] === 'Dedicated');

        if (dedicatedIndex > -1 && rewardsTally?.length > 1) {
            if (rewardsTally.count === numTotalRewards) {
                // Show dedicated at the end if other rewards were also given just once
                rewardsTally.push(rewardsTally.splice(dedicatedIndex, 1)[0]);
            }
            else {
                // show it at the start
                rewardsTally.unshift(rewardsTally.splice(dedicatedIndex, 1)[0]);
            }
        }

        const rewardsDurationMap = { 1: 6000, 2: 8500, 3: 10000, 4: 12000 };
        const rewardsStory = {
            component: 'rewardsTally',
            data: {
                heading: ``,
                rewards: rewardsTally
            },
            duration: rewardsDurationMap[rewardsTally?.length] ?? 5000
        }

        const activityLevelsStoryIndex = stories.findIndex(story => story.component === 'activityByDay');
        if (rewardsTally?.length >= 3) {
            rewardsStory.data.heading = `This week you...`;
            // insert rewards right before flow count story.
            const flowCountStoryIndex = stories.findIndex(story => story.component === 'flowCount');
            stories.splice(flowCountStoryIndex, 0, rewardsStory);
            // update flag for flowcount story heading
            flowCountStory.data.isAfterRewards = true;
        }
        else {
            if (!rewardsTally || numTotalRewards < 2) {
                return;
            }
            // when there are atleast 2 distinct rewards or 1 reward that was given more than once, show it before activity levels
            stories.splice(activityLevelsStoryIndex, 0, rewardsStory);
        }
    }
    //#endregion

    //#region Get text from storage/gpt

    // Retrieve previously stored reflection from DB
    async fetchStoredReflection() {
        const storedReflection = await getReflection(
            this.userId,
            "week_reflection",
            formatDateForStorage(this.weekStartDate)
        );
        return storedReflection;
    }

    async updateStoriesWithFetchedContent(storedReflection) {
        if (!this.currentWeekFlows) return null;

        // update story with stored reflection question/answer
        const updatedStoriesWithSavedReflections = this.updateStoriesWithSavedReflections(storedReflection);
        const updatedStoriesWithGptText = await this.updateStoriesWithGptText(storedReflection, this.currentWeekFlows.length ?? 0);

        // refresh the stored stories if they were based on a stale number of flows
        if (storedReflection.numFlows && (storedReflection.numFlows !== this.currentWeekFlows.length)) {
            this.saveStories(); // should this be awaited?
        }

        return {
            reflectionId: storedReflection?._id ?? null,
            updatedStoriesWithSavedReflections,
            updatedStoriesWithGptText
        };
    }

    updateStoriesWithSavedReflections(storedReflection) {
        if (!storedReflection || ('error' in storedReflection)) {
            return;
        }

        let updatedStories = [];
        storedReflection.stories?.forEach(story => {
            if (story.data?.comments?.length > 0) {
                let storyToUpdate;
                if (story.component?.includes('recurringCauses')) {
                    const filteredStories = this.stories.filter(story => story.component === 'recurringCauses');
                    storyToUpdate = filteredStories[story.index];
                    if (!storyToUpdate) {
                        console.error(`No cause story found for index ${story.index}`);
                        return;
                    }
                }
                else {
                    storyToUpdate = this.stories.find(element => element.component === story.component);
                }

                storyToUpdate.data.comments = story.data.comments;
                updatedStories.push(storyToUpdate);
            }
            else if (story.data?.answer?.length > 0) { // reflection/week title story
                let storyToUpdate = this.stories.find(element => element.component === story.component);
                storyToUpdate.data = story.data;
                updatedStories.push(storyToUpdate);
            }
        });

        return updatedStories;
    }

    /**
     * @returns {Array} Empty array if nothing is elgible for update or we couldn't fetch content to update
     */
    async updateStoriesWithGptText(storedReflection, currentNumFlows) {
        const eligibleComponentNames = this.getEligibleComponents();
        if (eligibleComponentNames.length === 0) {
            return [];
        }

        let storiesToUpdateWithSavedGptText = [];
        // if there are stored descriptions AND the num flows has not changed, then use them.
        if (storedReflection?.stories && storedReflection?.numFlows === currentNumFlows) {
            storiesToUpdateWithSavedGptText = storedReflection.stories
                .filter(story => eligibleComponentNames.includes(story.component) && story.data?.trend?.length > 0)
                .map(story => ({
                    targetedComponent: story.component,
                    text: story.data.trend
                }));
        }

        const storiesToUpdateWithNewGptText = await this.fetchMissingDescriptions(eligibleComponentNames, storiesToUpdateWithSavedGptText);
        const storiesToUpdate = [...storiesToUpdateWithSavedGptText, ...storiesToUpdateWithNewGptText];

        // update the stories
        let updatedStories = [];
        storiesToUpdate.forEach(element => {
            let story = this.stories.find(story => story.component === element.targetedComponent)
            story.data.trend = element.text;
            updatedStories.push(story);
        });

        return updatedStories;
    }

    /**
     * Returns the array of component names that are eligible to receive GPT-generated text.
     */
    getEligibleComponents() {
        let eligibleComponents = [];
        if (this.numActiveDays > 2) {
            eligibleComponents = ['energyTrend', 'pleasantnessTrend'];
        }

        if (this.orderedPeakValleyInfo?.length > 2 || this.doAnyPeaksValleysFallOnSameDay(this.orderedPeakValleyInfo)) {
            eligibleComponents.push('peakValley');
        }

        return eligibleComponents;
    }

    doAnyPeaksValleysFallOnSameDay(peakValleyOccurences) {
        /*
        How it works:
        1. some() goes through each object in the array.
        2. findIndex() finds the first time the current object's day value is found.
        3. If the found index is different from the current index (i), 
        it means a day duplicate exists.
        4. .some() stops early and returns true as soon as a duplicate is detected.
        */
        const isOnSameDay = peakValleyOccurences?.some((occurence, i) =>
            peakValleyOccurences.findIndex(obj => occurence.day === obj.day) !== i
        );
    }

    async fetchMissingDescriptions(eligibleComponents, availableDescriptions) {
        const missingComponents = eligibleComponents.filter(
            component => !availableDescriptions?.some(obj => obj.targetedComponent === component)
        );

        if (missingComponents.length === 0) return [];

        const missingDescriptions = await getStoryGPTData(
            this.currentWeekFlows,
            this.orderedPeakValleyInfo,
            missingComponents
        );

        return missingDescriptions ?? [];
    }

    async saveStories() {
        const dateForStorage = formatDateForStorage(this.weekStartDate);
        const weekReflection = {
            type: 'week_reflection',
            userId: this.userId,
            startDate: dateForStorage,
            numFlows: this.currentWeekFlows?.length,
            stories: this.stories
        }

        const result = await createOrUpdateReflection(weekReflection);
        return result;
    }
    //#endregion

    //#region Story helper functions
    getIntroStoryData(weekStart, weekFlows) {
        const weekDates = getWeek(weekStart);
        let daysAndColors = [];
        weekDates.forEach(date => {
            const color = calculateDayColor(date, weekFlows);
            const day = JSNumberToDayMapping[date.getDay()].Abbrv;
            daysAndColors.push({ day, color });
        });

        const titleInfo = getInfoAboutWeekInMonth(weekStart);

        return {
            titleInfo,
            daysAndColors
        };
    }

    getFlowCountStoryData(currentWeekFlows, pastWeekFlows, isFirstWeek) {
        let storyData = {
            component: "flowCount",
            data: {
                isAfterRewards: false,
                numFlowsInWeek: currentWeekFlows.length
            }
        };
        if (isFirstWeek || pastWeekFlows?.length === 0) {
            return storyData;
        }

        const flowCountDiff = getFlowCountDiff(pastWeekFlows, currentWeekFlows);
        storyData.data.flowCountDiff = flowCountDiff;

        if (!flowCountDiff) {
            storyData.data.subheading = '';
        }
        else if (flowCountDiff === 0) {
            storyData.data.subheading = "Pretty consistent with last week";
        }
        else if (flowCountDiff > 0) {
            storyData.data.subheading = `⬆️ That's ${flowCountDiff} more than last week`;
        }
        else {
            const flowDiff = Math.abs(flowCountDiff);
            storyData.data.subheading = `${flowDiff} less than last week`;
        }

        return storyData;
    }

    updateFlowCountStoryData(flowCountStoryData, keyword) {
        if (!flowCountStoryData.flowCountDiff) {
            flowCountStoryData.data.subheading = `All touched on ${keyword}.`
            return;
        }

        if (flowCountStoryData.flowCountDiff === 0) {
            flowCountStoryData.data.subheading = `All of them about ${keyword}, and the same number as last week.`
            return;
        }
        else {
            flowCountStoryData.data.subheading = flowCountStoryData.data.flowCountDiff < 0
                ? `${flowCountStoryData.data.subheading}, and all of them about ${keyword}`
                : `${flowCountStoryData.data.flowCountDiff} more than last week, and all of them about ${keyword}`;
        }
    }

    getPleasantPercentageStoryData(currentWeekFlows, pastWeekFlows) {
        const data = getPleasantUnpleasantPercentages(currentWeekFlows);
        const percentDiff = getPleasantPercentDiff(pastWeekFlows, currentWeekFlows);

        if (typeof data.pleasant.percentage !== 'number' || typeof data.unpleasant.percentage !== 'number') {
            return undefined;
        }
        return {
            component: "pleasantPercentage",
            data: {
                pleasantPercentage: data.pleasant.percentage,
                unpleasantPercentage: data.unpleasant.percentage,
                percentDiff: !percentDiff ? undefined
                    : percentDiff > 0 ? `That's ${percentDiff}% more pleasant than last week!`
                        : `That's a ${Math.abs(percentDiff)}% dip from last week.`
            },
            duration: 6500,
        }
    }

    getRecurringFeelingsStory(currentWeekFlows) {
        const pleasantFeelings = getMostRecurringCausesInCategory(currentWeekFlows, 'pleasant', 'feelings_exploration');
        const unpleasantFeelings = getMostRecurringCausesInCategory(currentWeekFlows, 'unpleasant', 'feelings_exploration');
        if (pleasantFeelings || unpleasantFeelings) {
            return {
                component: 'recurringFeelings',
                data: {
                    heading: 'Recurring feelings this week',
                    pleasant: pleasantFeelings,
                    unpleasant: unpleasantFeelings
                }
            }
        }
    }

    getSupportStory(currentWeekFlows) {
        let causeCounts = getCauseCountInCategory(currentWeekFlows, 'support');
        const supportHeadings = [
            "What you needed when things felt tough",
            "What you reached for in the hard moments",
            "What you needed when things felt hard",
            "The support you craved when things were tough",
            "What you found yourself needing most this week",
        ];

        if (causeCounts?.length < 1) {
            return;
        }

        for (let i = 0; i < causeCounts.length; i++) {
            let cause = causeCounts[i][0];
            if (cause.toLowerCase().includes(':')) {
                cause = cause.split(':')[1];
                if (cause.length <= 125) {
                    causeCounts[i][0] = cause;
                }
                else {
                    if (causeCounts.length < 3) {
                        causeCounts[i][0] = cause;
                    }
                    else {
                        // remove element
                        causeCounts.splice(i, 1);
                    }
                }
            }
        }
        const duration = causeCounts?.length > 4 ? 7000 : 5000
        return {
            component: 'support',
            data: {
                heading: supportHeadings[getRandomNumber(0, 5)],
                causeCounts: causeCounts,
            },
            duration: duration
        }
    }

    getStreakStories(entries, currentWeekFlows) {
        let stories = [];

        // Get longest streak in week
        const lastFlowOfGivenWeek = currentWeekFlows[currentWeekFlows.length - 1];
        const weekEntries = currentWeekFlows.map(flow => flow.entry);
        const streakInWeek = getLongestStreakOfDays(weekEntries, lastFlowOfGivenWeek.date);
        if (!streakInWeek) {
            return stories;
        }

        // else check if the streak is part of an ongoing active streak
        // The date that we start looking backwards from to see if there was a streak active. Default is today.
        let streakEvaluationDate = new Date();
        const activeStreak = entries ? getActiveStreak(entries, streakEvaluationDate) : null;
        const weekStartDate = getWeekStartDate(lastFlowOfGivenWeek.date);
        const isWeekCurrent = getNumDaysInBetween(weekStartDate, streakEvaluationDate) <= 7;

        let heading, subheading, numDaysInStreak, supportingImage;
        // If streak is currently active AND greater than the longest streak this week
        if (activeStreak && isWeekCurrent && activeStreak.streakLength >= streakInWeek.streakLength) {
            // Then we'll show the active streak info
            numDaysInStreak = activeStreak.streakLength;
            supportingImage = { name: 'Fire', count: numDaysInStreak < 6 ? numDaysInStreak : 5 } // count is capped at 5.
            heading = `You are on a hot streak of`;
            subheading = `Feed the fire that guides you towards balance instead of burnout.`;
        }
        else { // show the longest streak in week
            numDaysInStreak = streakInWeek.streakLength;
            supportingImage = { name: 'Consistency Reward', count: numDaysInStreak }
            heading = `And your longest streak was`
            subheading = !isWeekCurrent ?
                `From ${JSNumberToDayMapping[streakInWeek.startDate.getDay()].Full} to ${JSNumberToDayMapping[streakInWeek.endDate.getDay()].Full}`
                : numDaysInStreak > 3 ? `Way to be consistent!` : `The more consistent you are, the more clarity you gain.`;
        }

        stories.push({
            component: 'dayStreak',
            data: {
                numDaysInStreak: numDaysInStreak,
                isActiveHighlighted: supportingImage.name === 'Fire',
                heading: heading,
                subheading: subheading,
                supportingImage: supportingImage
            }
        });

        // Determine if Habit hero streak is on.
        // Filter the entries to only be until this end date to pass to the function
        const lastFlowIndex = entries.findIndex(entry => entry._id === lastFlowOfGivenWeek.entry._id);
        const entriesUntilThisWeek = lastFlowIndex === -1 ? entries : entries.slice(0, lastFlowIndex + 1);
        const habitHeroStreak = getHabitHeroStreak(entriesUntilThisWeek, lastFlowOfGivenWeek.date);
        if (habitHeroStreak) {
            const heading = habitHeroStreak > 1 ?
                `You've been a Habit Hero for ${habitHeroStreak} weeks straight!`
                : `You're a Habit Hero!`
            // "Because winners don't rely on motivation to seek change, they build habits that do it for them."; // placeholder
            "Because pros don’t hope for the best, they build habits that guarantee it."

            const message = HabitHeroMessages.Streak[getRandomNumber(0, HabitHeroMessages.Streak.length)];

            stories.push({
                component: 'habitHeroStreak',
                data: {
                    heading,
                    message,
                    hasHabitHeroStreak: habitHeroStreak > 1,
                },
                duration: habitHeroStreak > 1 ? 6500 : 5000
            });
        }

        return stories;
    }
    //#endregion

    async saveReflection(textInputs) {
        Object.entries(textInputs)?.forEach(([key, value]) => {
            let storyToUpdate;
            if (key.includes('recurringCauses')) {
                const componentName = key.slice(0, -1);
                let index = Number(key.slice(-1));
                if (isNaN(index)) {
                    console.error(`index is NaN, for key ${key}`)
                    return;
                }
                const filteredStories = this.stories.filter(story => story.component === componentName);
                if (filteredStories.length > index) {
                    storyToUpdate = filteredStories[index];
                }
                else {
                    console.error(`index is greater than cause stories found, ${index}`);
                    return;
                }
            }
            else {
                storyToUpdate = this.stories.find(story => story.component === key);
            }

            if (key === 'reflectionInput' || key === 'weekTitle') {
                storyToUpdate.data.answer = value;
            }
            else {
                storyToUpdate.data.comments = value
            }
        })

        const result = await this.saveStories();
        if (result?.reflectionId) {
            this.isReflectionSaved = true;
            return result.reflectionId;
        }

        return null;
    }

    addDateIdsToComments(comments) {
        comments.forEach(comment => {
            if (typeof comment === 'string') {
                // update it to be an object with date and id
                const date = new Date();
                comment = {
                    id: crypto.randomUUID(),
                    date: date,
                    text: comment
                }
            }
        })

        return comments;
    }
}
